import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import "./SearchPage.css";
import { useContext, useEffect, useRef, useState } from "react";
import mainApi from "../../assets/api/MainApi";
import MiniPreloader from "../MiniPreloader/MiniPreloader";
import Products from "../Catalog/Products/Products";
import { ConfigContext } from "../../assets/contexts/configContext";
import CategoryCard from "../Catalog/CategoryCard/CategoryCard";
import { CATALOG_MAIN_LINK } from "../../assets/utils/constants";

function SearchPage() {
    const config = useContext(ConfigContext);
    const { initialConstants } = config;
    const { SHOP_ID_FOR_API, SHOP_NAME } = initialConstants;

    const [preloaders, setPreloaders] = useState({
        more: false,
    });

    const [searchParams, setSearchParams] = useSearchParams();
    const value = searchParams.get("value") || "";

    // Local input state so user can edit query
    const [searchValue, setSearchValue] = useState(value);

    const [searchItems, setSearchItems] = useState([]);
    const [searchCategories, setSearchCategories] = useState([]);
    const [isSearchPreloaderVisible, setSearchPreloaderVisible] = useState(true);
    const [isSearchCatPreloaderVisible, setSearchCatPreloaderVisible] =
        useState(true);

    // Keep input in sync when URL param changes (e.g. from external link)
    useEffect(() => {
        setSearchValue(value || "");
    }, [value]);

    useEffect(() => {
        setSearchPreloaderVisible(true);
        setSearchCatPreloaderVisible(true);

        mainApi
            .getSearch({
                shop_id: SHOP_ID_FOR_API,
                string: value,
                limit: 25,
                content_types: ["published"],
            })
            .then((res) => {
                console.log(res.data);
                setSearchItems(res);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setSearchPreloaderVisible(false);
            });

        mainApi
            .getSearch({
                shop_id: SHOP_ID_FOR_API,
                string: value,
                limit: 25,
                content_types: ["categories"],
            })
            .then((res) => {
                console.log(res.data);
                setSearchCategories(res.data);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setSearchCatPreloaderVisible(false);
            });
    }, [value, SHOP_ID_FOR_API]);

    const loadMoreRef = useRef();

    function getMoreProducts() {
        mainApi
            .getSearch({
                shop_id: SHOP_ID_FOR_API,
                scroll_id: searchItems.scroll_id,
                string: value,
                limit: 25,
                content_types: ["published"],
            })
            .then((res) => {
                console.log({ res, searchItems });
                setSearchItems((prevValue) => ({
                    ...prevValue,
                    data: prevValue.data.concat(res.data),
                    is_more: res.is_more,
                }));
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setSearchPreloaderVisible(false);
            });
    }

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && searchItems?.is_more) {
                    getMoreProducts();
                }
            },
            { root: null, rootMargin: "0px", threshold: 1.0 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
        };
    }, [searchItems]);

    // Submit handler for search form
    function handleSearchSubmit(e) {
        e.preventDefault();
        const trimmed = searchValue.trim();

        // If empty, you can decide: clear param or keep old
        // Here we just clear it
        if (!trimmed) {
            setSearchPreloaderVisible(true);
            setSearchCatPreloaderVisible(true);
            setSearchParams({});
            return;
        }

        setSearchParams({ value: trimmed });
    }

    // Clear input + results
    function handleClear() {
        setSearchValue("");
        setSearchParams({});
    }

    return (
        <div className={`search-page`}>
            <div className="search-page__content">
                {isSearchPreloaderVisible || isSearchCatPreloaderVisible ? (
                    <div className="search-drop__preloader">
                        <Helmet>
                            <title>{SHOP_NAME} - ...</title>
                        </Helmet>
                        <MiniPreloader />
                    </div>
                ) : (
                    <>
                        <Helmet>
                            <title>
                                {SHOP_NAME} - Search{value ? `: ${value}` : ""}
                            </title>
                        </Helmet>

                        {/* Search header with input */}
                        <div className="search-page__header">
                            <h2 className="search-page__title">Search</h2>
                            <form className="search-page__search-bar" onSubmit={handleSearchSubmit}>
                                <div className="search-page__search-input-wrapper">
                                    <input
                                        type="text"
                                        className="search-page__search-input"
                                        placeholder={`Search in ${SHOP_NAME}`}
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                    />
                                    {searchValue && (
                                        <button
                                            type="button"
                                            className="search-page__search-clear"
                                            onClick={handleClear}
                                            aria-label="Clear search"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className="search-page__search-button"
                                    disabled={!(searchValue.trim() && searchValue !== value)}
                                >
                                    Search
                                </button>
                            </form>

                            {value && (
                                <p className="search-page__subtitle">
                                    Showing results for: <span>"{value}"</span>
                                </p>
                            )}
                        </div>

                        {value ?
                            <>
                                {searchCategories && searchCategories.length > 0 ? (
                                    <div className="search-page__cats">
                                        {searchCategories.map((category, i) => (
                                            <CategoryCard
                                                key={category.id || category._id || category.slug || i}
                                                notInCatalog
                                                isSearch
                                                category={category}
                                                levels={[
                                                    CATALOG_MAIN_LINK,
                                                    ...category.parents
                                                        .slice(1)
                                                        .map((parent) => parent.translit_name),
                                                ]}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                                <Products
                                    items={searchItems}
                                    preloaders={{
                                        items: isSearchPreloaderVisible,
                                        subcategories: false,
                                    }}
                                    category={null}
                                    isSearch={true}
                                    path={"/"}
                                />

                                {searchItems && searchItems.data && searchItems.is_more ? (
                                    <button
                                        ref={loadMoreRef}
                                        className={`catalog__more ${preloaders.more ? "catalog__more_loading" : ""
                                            }`}
                                        type="button"
                                        onClick={() => {
                                            getMoreProducts();
                                        }}
                                    >
                                        {preloaders.more ? (
                                            <MiniPreloader isLinkColor={true} />
                                        ) : (
                                            "Load more"
                                        )}
                                    </button>
                                ) : null}
                            </>
                            : <p>Enter a search query</p>}




                    </>
                )}
            </div>
        </div>
    );
}

export default SearchPage;